/**
 * QUIL — Auth context + hook.
 * Kept in a non-component module so the provider file (AuthContext.tsx) only
 * exports a component (required for React Fast Refresh / eslint react-refresh).
 */
import { createContext, useContext } from 'react'
import type { AccountInfo } from '@azure/msal-browser'
import type { TokenGetter } from './authToken'
import type { BoundStore } from './storeBinding'

/**
 * Where this tab's local store stands (W12 A5). A signed-in account is matched to its store before
 * anything reads or syncs it: `resolving` while that runs, `ready` once the store is bound, `failed`
 * when it could not be opened (nothing was changed; the gate offers a retry). `none` is a signed-out
 * visitor, who has no store open.
 */
export type StoreState =
  | { status: 'none' }
  | { status: 'resolving' }
  | { status: 'ready'; store: BoundStore }
  | { status: 'failed'; error: Error }

export interface AuthContextValue {
  account: AccountInfo | null
  isInitializing: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  getAccessToken: TokenGetter
  storeState: StoreState
  /** Run the store resolution again after a failure. */
  retryStore: () => void
  /** Does this device already hold a library it knows is an account's, so it can be opened offline? */
  canOpenOffline: () => Promise<boolean>
  /** Open that library with no sign-in (the offline cold start). Null when there is none to open. */
  openOffline: () => Promise<BoundStore | null>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
